import { processConsentChangeFunction } from '@utiqLoader/consentHandling/getProcessConsentChangeFunction';
import { handleDataClear } from '@utiqLoader/helpers/handleDataClear';
import { addEventListener } from '@shared/api/sharedAPIEventFunctions';
import { utiqMachine } from '@machine/index';
import { getHostname } from '@shared/getUtiqHost';
import { getVersions } from '@shared/helpers/getVersions';

const getDidomiConsents = (
  Didomi: Didomi,
  didomiUtiqPurposeId: string,
  didomiUtiqVendorId: string,
) => {
  const currentUserStatus = Didomi.getCurrentUserStatus();
  const didomiConsentExists = !Didomi.shouldUserStatusBeCollected();
  /* We need to strip "c:" string from the config ID, as the newly used Didomi function also have it stripped in their data object */
  return {
    purposeConsent: didomiConsentExists
      ? currentUserStatus?.purposes?.[didomiUtiqPurposeId?.replace('c:', '')]
          ?.enabled
      : undefined,
    vendorConsent: didomiConsentExists
      ? currentUserStatus?.vendors?.[didomiUtiqVendorId?.replace('c:', '')]
          ?.enabled
      : undefined,
  };
};

export async function setupCMPDidomiBeforeUtiq(): Promise<void> {
  const checkDidomiConsents = (Didomi: Didomi) => {
    const Utiq = window.Utiq;
    if (!Utiq) throw new Error('window.Utiq === undefined');

    const { purposeConsent, vendorConsent } = getDidomiConsents(
      Didomi,
      Utiq?.config?.didomiUtiqPurposeId!,
      Utiq?.config?.didomiUtiqVendorId!,
    );

    if (purposeConsent === false || vendorConsent === false) {
      return {
        shouldRunDataClear: true,
      };
    } else if (purposeConsent === true && vendorConsent === true) {
      return {
        shouldInitializeUtiqFlow: true,
      };
    }
    return {};
  };

  return new Promise((done) => {
    window.didomiOnReady ||= [];
    window.didomiOnReady.push((Didomi: Didomi) => {
      const onConsentsCheckActions = checkDidomiConsents(Didomi);
      let isInitialized = Boolean(
        onConsentsCheckActions.shouldInitializeUtiqFlow,
      );
      if (isInitialized) {
        return done();
      } else {
        if (onConsentsCheckActions.shouldRunDataClear) {
          handleDataClear();
          return done();
        }
        /* Listen to the consent changed from Didomi */
        window.didomiEventListeners ||= [];
        window.didomiEventListeners.push({
          event: 'consent.changed',
          listener(context: { action: string }) {
            /* Execute Utiq consent processing only when the change comes from Didomi Manager to avoid consent change loops */
            if (context?.action === 'click' && !isInitialized) {
              const onConsentsCheckActions = checkDidomiConsents(Didomi);
              isInitialized = Boolean(
                onConsentsCheckActions.shouldInitializeUtiqFlow,
              );
              if (!isInitialized) {
                if (onConsentsCheckActions.shouldRunDataClear) {
                  handleDataClear();
                  return done();
                }
              }
              return done();
            }
          },
        });
      }
    });
  });
}

export async function setupCMPDidomiAfterUtiq() {
  const Utiq = window.Utiq;
  if (!Utiq) throw new Error('window.Utiq === undefined');

  const config = Utiq.config;
  if (!config) throw new Error('Utiq.config === undefined');

  const processConsent = () => {
    window.didomiOnReady ||= [];
    window.didomiOnReady.push((Didomi: Didomi) => {
      const { purposeConsent, vendorConsent } = getDidomiConsents(
        Didomi,
        config.didomiUtiqPurposeId!,
        config.didomiUtiqVendorId!,
      );

      const isConsentGranted = Boolean(purposeConsent && vendorConsent);

      const hostname = getHostname();

      if (!window.Utiq?.data?.popupContent?.version) return;
      if (!window.Utiq?.data?.popupContent?.versionPrevious) return;

      const consentVersion = getVersions(
        window.Utiq?.data?.popupContent?.version,
      ).majorVersion;

      const consentVersionPrevious = getVersions(
        window.Utiq?.data?.popupContent?.versionPrevious,
      ).majorVersion;

      if (!consentVersion) return;
      if (!consentVersionPrevious) return;

      processConsentChangeFunction(Utiq, isConsentGranted, {
        [hostname]: {
          name: 'didomiConsent',
          value: `purposeConsent=${purposeConsent};vendorConsent=${vendorConsent}`,
          consentVersion,
          consentVersionPrevious,
          consentParsed: isConsentGranted ? 'true' : 'false',
          consentState: isConsentGranted ? 'accepted' : 'rejected',
        },
      });
    });
  };

  // This is less-than-ideal, It relies on internal state of the machine in what should really be an agnostic-like binding layer.
  // But I don't really know how better to avoid the race condition of didomi overwriting the from-utiq-server consent denied response
  if (!utiqMachine.ctx.axios.log.find((x) => x.res?.status === 204))
    processConsent();

  /* Listen to the consent changed from Didomi */
  window.didomiEventListeners ||= [];
  window.didomiEventListeners.push({
    event: 'consent.changed',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listener: function (context: any) {
      /* Execute Utiq consent processing only when the change comes from Didomi Manager to avoid consent change loops */
      if (context?.action === 'click') {
        window.Utiq ||= {};
        window.Utiq.syncVersionCookies = true;
        processConsent();
      }
    },
  });

  const synchronizeDidomiConsentStatus = (
    isConsentGranted: boolean,
    utiqPurposeId: string,
    utiqVendorId: string,
  ) => {
    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(function (Didomi: Didomi) {
      const { purposeConsent, vendorConsent } = getDidomiConsents(
        Didomi,
        Utiq?.config?.didomiUtiqPurposeId!,
        Utiq?.config?.didomiUtiqVendorId!,
      );

      const didomiNeedsSync =
        isConsentGranted !== !!purposeConsent ||
        isConsentGranted !== !!vendorConsent;

      if (!Didomi.shouldUserStatusBeCollected() && didomiNeedsSync) {
        const transaction = Didomi.openCurrentUserStatusTransaction();
        if (isConsentGranted) {
          transaction.enablePurpose(utiqPurposeId);
          transaction.enableVendor(utiqVendorId.replace('c:', ''));
        } else {
          transaction.disablePurpose(utiqPurposeId);
          transaction.disableVendor(utiqVendorId.replace('c:', ''));
        }
        transaction.commit();
      }
    });
  };

  addEventListener('onConsentChanging', (evt: unknown) => {
    if (
      !(
        typeof evt === 'object' &&
        evt !== null &&
        'isConsentGranted' in evt &&
        typeof evt.isConsentGranted === 'boolean'
      )
    )
      return;

    const { isConsentGranted } = evt;
    synchronizeDidomiConsentStatus(
      isConsentGranted,
      config.didomiUtiqPurposeId!,
      config.didomiUtiqVendorId!,
    );
    processConsent();
  });

  /* Synchronize the Didomi consent when the update is finished - covers data clear and 204 data call consent rejection from ConsentHub */
  addEventListener('onConsentUpdateFinished', (evt: unknown) => {
    if (
      !(
        typeof evt === 'object' &&
        evt !== null &&
        'isConsentGranted' in evt &&
        typeof evt.isConsentGranted === 'boolean'
      )
    )
      return;

    const { isConsentGranted } = evt;

    synchronizeDidomiConsentStatus(
      isConsentGranted,
      config.didomiUtiqPurposeId!,
      config.didomiUtiqVendorId!,
    );
  });
}
